import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { useLocale } from '@/app/[lang]/providers/LocaleProvider';
import { HomePage } from "../type/type";
import ProposeCard from "@/components/ui/card";

export default function DrivenPurpose({
  data,
  isLoading,
  isError,
}: {
  data: HomePage | undefined;
  isLoading: boolean;
  isError: boolean;
}) {
  const [hovered, setHovered] = useState<number | null>(null);

  if (isLoading || isError || !data) {
    return null; // Or a loading skeleton
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-0 bg-[url('/images/bg-driven.png')] bg-no-repeat bg-right">
      <div className="flex flex-col gap-10 md:gap-16">
        <div className="flex flex-col gap-8 md:flex-row md:gap-20 md:items-center">
          <div
            className="relative w-full max-w-135 h-64 sm:h-80 md:h-110"
            style={{ aspectRatio: '27 / 22' }}
          >
            <Image
              src={data.purpose_image ? `${data.purpose_image}` : "/images/driven-by-purpose.png"}
              alt={data.purpose_image_alt || "Driven by purpose"}
              fill
              className="object-contain"
              priority
              sizes="(max-width: 768px) 90vw, 540px"
            />
          </div>

          <div className="flex flex-col gap-6 md:gap-10">
            <h2 className="text-primary-800 font-bold text-3xl md:text-5xl">
              {data.purpose_title}
            </h2>
            <div className="text-secondary" dangerouslySetInnerHTML={{ __html: data.purpose_desc }} />
            {data.purpose_cta_text && data.purpose_cta_url && (() => {
              const isExternal = /^https?:\/\//.test(data.purpose_cta_url);
              return isExternal ? (
                <a
                  href={data.purpose_cta_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-fit px-6 py-3 bg-primary-700 text-white font-semibold rounded-lg hover:bg-primary-800 transition-colors"
                >
                  {data.purpose_cta_text}
                </a>
              ) : (
                <Link
                  href={data.purpose_cta_url}
                  className="w-fit px-6 py-3 bg-primary-700 text-white font-semibold rounded-lg hover:bg-primary-800 transition-colors"
                >
                  {data.purpose_cta_text}
                </Link>
              );
            })()}
          </div>
        </div>

        <div className="flex flex-wrap gap-4 md:gap-6 w-full max-w-7xl mx-auto">
          {Array.isArray(data.purpose_list) && data.purpose_list.map((item, idx) => (
            <div
              key={idx}
              className="transition-all duration-500 flex-1 basis-full sm:basis-[calc(50%-0.5rem)] lg:basis-0 min-w-30 max-w-full lg:max-w-87.5"
              onMouseEnter={() => setHovered(idx)}
              onMouseLeave={() => setHovered(null)}
              style={{ cursor: 'pointer' }}
            >
              <ProposeCard
                title={item.title}
                total={item.total}
                content={item.desc}
                className={`w-full min-h-50 max-h-85 ${hovered === idx ? 'bg-[#094cad]' : ''}`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

